/**
 * Theme Toggle Switch Component
 *
 * @module modules/user/presentation/shared/components/ThemeToggleSwitch
 *
 * Custom styled toggle switch for dark/light mode.
 */

import Box from '@mui/material/Box';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { memo } from 'react';

interface ThemeToggleSwitchProps {
  /** Whether dark mode is enabled */
  isDark: boolean;
  /** Optional click handler (for stopping propagation) */
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * Theme Toggle Switch Component
 *
 * A styled toggle switch that shows the current theme state.
 *
 * @example
 * ```tsx
 * <ThemeToggleSwitch isDark={isDarkMode} onClick={(e) => e.stopPropagation()} />
 * ```
 */
export const ThemeToggleSwitch = memo(function ThemeToggleSwitch({
  isDark,
  onClick,
}: ThemeToggleSwitchProps) {
  const muiTheme = useMuiTheme();

  return (
    <Box
      onClick={onClick}
      sx={{
        width: 36,
        height: 18,
        borderRadius: 9,
        bgcolor: isDark ? 'primary.main' : 'action.disabledBackground',
        position: 'relative',
        cursor: 'pointer',
        transition: muiTheme.transitions.create('background-color'),
        display: 'flex',
        alignItems: 'center',
        px: 0.25,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: isDark ? 'translateX(18px)' : 'translateX(0)',
          transition: muiTheme.transitions.create('transform'),
          boxShadow: 1,
        }}
      >
        {isDark ? (
          <DarkModeIcon sx={{ fontSize: 8, color: 'primary.main' }} />
        ) : (
          <LightModeIcon sx={{ fontSize: 8, color: 'warning.main' }} />
        )}
      </Box>
    </Box>
  );
});

